import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { State } from "@/state";
import { useCellRegistry } from "./useContextTree.cells";
import {
  ESCAPE_COMBO,
  ESCAPE_COMBO_KEY,
  attachGlobalKeyboardListener,
  comboToKey,
  getOS,
  nextRegistrationId,
  normalizeCombo,
  registerHotkey,
  resolveCombos,
  unregisterHotkey,
  useContextHotkeys,
} from "./useContextTree.hotkeys";
import type {
  HotkeyCallback,
  HotkeyConfig,
  IsEnabledFn,
} from "./useContextTree.hotkeys";
import type { CellNavigationOptions } from "./useContextTree.cells";

// -------------------------
// Types
// -------------------------

export type ContextNode = {
  id: string;
  parent: ContextNode | null;
  /** When true, single printable-key hotkeys from ancestor contexts
   *  are suppressed while this context (or a descendant) has focus. */
  capturesInput?: boolean;
  /** When true, ancestor (non-global) hotkeys above this node are suppressed
   *  while this context (or a descendant) has focus. */
  resetHotkeys?: boolean;
  /** When true with ``resetHotkeys``, also suppress ``HotkeyScope.global``
   *  hotkeys (overlays / dialogs). Match-style panels leave this false so
   *  app shortcuts like ⌘K still work. */
  suppressGlobalHotkeys?: boolean;
};

export type { CellNavigationOptions, CellProps } from "./useContextTree.cells";

// -------------------------
// CSS Utilities
// -------------------------

/**
 * Sanitize a key for use in CSS class names and variable names.
 * Replaces non-alphanumeric characters (excluding hyphen) with underscores.
 */
export const sanitizeKeyForCSS = (key: string): string => {
  return String(key).replace(/[^a-zA-Z0-9-]/g, "_");
};

// -------------------------
// Scope checks
// -------------------------

export const HotkeyScope = {
  global: (_handler: ContextNode | null, _active: ContextNode | null) => true,
  nearest: (handler: ContextNode | null, active: ContextNode | null) =>
    handler === active,
  within: (handler: ContextNode | null, active: ContextNode | null) =>
    isAncestorOrSelf(handler, active),
};

// -------------------------
// Global Focus Tracking (Simplified)
// -------------------------

const focusSubscribers = new Set<() => void>();
let globalFocusListenerAttached = false;

const lastFocusedByContext = new Map<string, HTMLElement>();

/**
 * Generate className for a column (matches useResizeColumn's CSS variable pattern)
 */
export const getContextClassnameForGrid = (
  objectKey: string,
  contextId: string,
) => {
  if (!objectKey) return "";
  return `${contextId}-column-${sanitizeKeyForCSS(objectKey)}`;
};

function notifyFocusSubscribers() {
  focusSubscribers.forEach((cb) => cb());
}

function attachGlobalFocusListener() {
  if (globalFocusListenerAttached) return;
  globalFocusListenerAttached = true;

  document.addEventListener(
    "focusin",
    () => {
      const el = document.activeElement as HTMLElement;
      if (el && el !== document.body) {
        const ctxNode = findNearestContextNode(el);
        if (ctxNode) {
          lastFocusedByContext.set(ctxNode.id, el);
        }
      }
      notifyFocusSubscribers();
    },
    true,
  );
  document.addEventListener("focusout", notifyFocusSubscribers, true);
  window.addEventListener("blur", notifyFocusSubscribers);
}

export const contextNodeMap = new WeakMap<Element, ContextNode>();

export function findNearestContextNode(el: Element | null): ContextNode | null {
  let current = el;
  while (current) {
    const node = contextNodeMap.get(current);
    if (node) return node;
    current = current.parentElement;
  }
  return null;
}

export function isAncestorOrSelf(
  ancestor: ContextNode | null,
  descendant: ContextNode | null,
): boolean {
  let current = descendant;
  while (current) {
    if (current === ancestor) return true;
    current = current.parent;
  }
  return false;
}

/** True when the event target lives under ``overlayNode`` or a nested context. */
export function isEventInsideContext(
  event: Event,
  overlayNode: ContextNode | null,
): boolean {
  if (!overlayNode) return false;
  const target = event.target;
  if (!(target instanceof Element)) return false;
  return isAncestorOrSelf(overlayNode, findNearestContextNode(target));
}

export function countDescendantDepth(
  ancestor: ContextNode | null,
  descendant: ContextNode | null,
): number {
  if (!ancestor || !descendant) return -1;

  let current = descendant;
  let depth = 0;

  while (current) {
    if (current === ancestor) return depth;
    current = current.parent;
    depth++;
  }

  return -1;
}

/** Open overlay scopes with ``resetHotkeys`` (e.g. portaled poppers). */
export const openResetHotkeyOverlays = new Set<ContextNode>();

export function findDeepestOpenResetHotkeysDescendant(
  ancestor: ContextNode | null,
): ContextNode | null {
  if (!ancestor) return null;

  let deepest: ContextNode | null = null;
  let deepestDepth = -1;

  for (const openNode of openResetHotkeyOverlays) {
    const depth = countDescendantDepth(ancestor, openNode);
    if (depth >= 0 && depth > deepestDepth) {
      deepest = openNode;
      deepestDepth = depth;
    }
  }

  return deepest;
}

export function hasOpenResetHotkeysOverlayInSubtree(
  ancestor: ContextNode | null,
): boolean {
  return findDeepestOpenResetHotkeysDescendant(ancestor) !== null;
}

/** Skip list/row focus reclaim while a portaled popper owned by this scope is open. */
export function shouldDeferListFocusReclaim(
  listElement: HTMLElement | null,
): boolean {
  if (openResetHotkeyOverlays.size > 0) {
    return true;
  }
  if (!listElement) {
    return false;
  }
  const listNode = findNearestContextNode(listElement);
  return hasOpenResetHotkeysOverlayInSubtree(listNode);
}

/** Nearest ``resetHotkeys`` boundary for the focused context — walks up
 *  from ``activeNode``, then checks for open descendant overlays (e.g. a
 *  popper portaled outside the anchor's DOM subtree). */
export function findResetHotkeysBoundary(
  activeNode: ContextNode | null,
): ContextNode | null {
  let resetBoundary: ContextNode | null = null;
  let walk: ContextNode | null = activeNode;
  while (walk) {
    if (walk.resetHotkeys) {
      resetBoundary = walk;
      break;
    }
    walk = walk.parent;
  }

  if (!resetBoundary && activeNode) {
    resetBoundary = findDeepestOpenResetHotkeysDescendant(activeNode);
  }

  return resetBoundary;
}

const OVERLAY_SUBMIT_SELECTOR = "[data-context-overlay-submit]";

// -------------------------
// Shared constants
// -------------------------

export const ContextTreeContext = createContext<ContextNode | null>(null);

const EMPTY_HOTKEYS: HotkeyConfig[] = Object.freeze(
  [],
) as unknown as HotkeyConfig[];

const FOCUS_OUTSIDE: FocusState = {
  isFocusedWithin: false,
  hasActiveDescendant: false,
};

interface FocusState {
  isFocusedWithin: boolean;
  hasActiveDescendant: boolean;
}

export const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';

// ==========================================================================
// useContextScope — core context tree node + Provider/Scope wrappers
// ==========================================================================

interface UseContextScopeOptions {
  capturesInput?: boolean;
  bridge?: boolean;
  resetHotkeys?: boolean;
  /** With ``resetHotkeys``, also block ``HotkeyScope.global`` handlers. */
  suppressGlobalHotkeys?: boolean;
  /**
   * With ``resetHotkeys``: while open, this scope also owns keys pressed with
   * focus outside it, in the context it hangs from - as a popper owns its
   * anchor's, focus having stayed there. Defaults to true. A panel beside
   * the page that leaves focus free passes false: it owns the keys pressed
   * in it, and no others.
   */
  claimsKeyboard?: boolean;
  trapFocus?: boolean;
  hotkeys?: HotkeyConfig[];
  /** When provided, registers an Escape hotkey scoped to this context.
   *  On Escape, focus is restored to the parent context first (via focusParent
   *  or popFocusOrigin in bridge mode) unless ``restoreFocusOnEscape`` is
   *  ``"none"``, then this callback is invoked. Pass ``"none"`` when the
   *  consumer restores focus after teardown (panel collapse, TipTap unmount).
   *  The callback is stored in a ref internally — no need for useMemo or useRef
   *  on the consumer side. Pass `undefined` to disable escape handling. */
  onEscape?: () => void;
  /** When ``none``, skip focus restore on Escape so the consumer can restore
   *  after close/teardown (``focusRestoreRef`` / deferred ``popFocusOrigin``). */
  restoreFocusOnEscape?: "parent" | "none";
}

export function useContextScope(options: UseContextScopeOptions = {}) {
  const rawId = useId();
  const id = rawId.replace(/:/g, "");
  const parentFromContext = useContext(ContextTreeContext);
  const {
    capturesInput = false,
    bridge = false,
    resetHotkeys = false,
    suppressGlobalHotkeys = false,
    claimsKeyboard = true,
    trapFocus = false,
    hotkeys = EMPTY_HOTKEYS,
    onEscape,
    restoreFocusOnEscape = "parent",
  } = options;

  const bridgeOrigin = State((s) =>
    bridge ? s.focusOriginStack[s.focusOriginStack.length - 1] : null,
  );
  const parentNode = bridge
    ? (bridgeOrigin?.contextNode as ContextNode | null)
    : parentFromContext;

  const node = useMemo<ContextNode>(
    () => ({
      id: rawId,
      parent: parentNode,
      capturesInput,
      resetHotkeys,
      suppressGlobalHotkeys: resetHotkeys && suppressGlobalHotkeys,
    }),
    [rawId, parentNode, capturesInput, resetHotkeys, suppressGlobalHotkeys],
  );

  const nodeRef = useRef<ContextNode>(node);
  nodeRef.current = node;

  useEffect(() => {
    if (!resetHotkeys || !claimsKeyboard) return;
    openResetHotkeyOverlays.add(node);
    return () => {
      openResetHotkeyOverlays.delete(node);
    };
  }, [resetHotkeys, claimsKeyboard, node]);

  const elementRef = useRef<HTMLElement | null>(null);

  if (elementRef.current && contextNodeMap.get(elementRef.current) !== node) {
    contextNodeMap.set(elementRef.current, node);
  }

  // -------------------------
  // Element Ref Management
  // -------------------------

  const setElementRef = useCallback((el: HTMLElement | null) => {
    if (elementRef.current && elementRef.current !== el) {
      contextNodeMap.delete(elementRef.current);
    }

    elementRef.current = el;

    if (el) {
      contextNodeMap.set(el, nodeRef.current);
    }
  }, []);

  const refObject = useMemo(
    () => ({
      get current(): HTMLDivElement | null {
        return elementRef.current as HTMLDivElement | null;
      },
      set current(el: HTMLDivElement | null) {
        setElementRef(el);
      },
    }),
    [setElementRef],
  );

  // -------------------------
  // Focus Trapping (Tab key interception)
  // -------------------------

  useEffect(() => {
    if (!trapFocus) return;

    const container = elementRef.current;
    if (!container) return;

    const getFocusable = (): HTMLElement[] =>
      Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.closest("[inert]") && el.offsetParent !== null);

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (!container.contains(document.activeElement)) return;

      const focusable = getFocusable();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [trapFocus]);

  // Stable refs so Provider/Scope always read the latest values
  const stableNodeRef = useRef(node);
  stableNodeRef.current = node;
  const stableIdRef = useRef(id);
  stableIdRef.current = id;
  const stableSetElementRefRef = useRef(setElementRef);
  stableSetElementRefRef.current = setElementRef;

  const [Provider] = useState(
    () =>
      ({ children }: { children: React.ReactNode }) => (
        <ContextTreeContext.Provider value={stableNodeRef.current}>
          {children}
        </ContextTreeContext.Provider>
      ),
  );

  const [Scope] = useState(() =>
    React.forwardRef<
      HTMLDivElement,
      React.HTMLAttributes<HTMLDivElement> & { children?: React.ReactNode }
    >((props, externalRef) => {
      const { children, ...rest } = props;
      return (
        <ContextTreeContext.Provider value={stableNodeRef.current}>
          <div
            ref={(el) => {
              stableSetElementRefRef.current(el);
              if (typeof externalRef === "function") externalRef(el);
              else if (externalRef)
                (
                  externalRef as React.MutableRefObject<HTMLDivElement | null>
                ).current = el;
            }}
            data-context-id={stableIdRef.current}
            {...rest}
          >
            {children}
          </div>
        </ContextTreeContext.Provider>
      );
    }),
  );

  // -------------------------
  // Inline hotkey registration
  // -------------------------

  const hotkeyCallbacksRef = useRef(new Map<string, HotkeyCallback>());
  const hotkeyIsEnabledRef = useRef(new Map<string, boolean | IsEnabledFn>());

  if (hotkeys !== EMPTY_HOTKEYS) {
    for (const h of hotkeys) {
      hotkeyCallbacksRef.current.set(h.id, h.callback);
      hotkeyIsEnabledRef.current.set(h.id, h.isEnabled ?? true);
    }
  }

  const hotkeyStructuralKey =
    hotkeys === EMPTY_HOTKEYS
      ? ""
      : hotkeys
          .map(
            (h) =>
              `${h.id}:${JSON.stringify(h.combos)}:${h.scopeCheck ? "c" : "w"}`,
          )
          .join(",");

  const inlineHotkeyIdsRef = useRef<string[]>([]);
  useEffect(() => {
    if (!hotkeyStructuralKey) return;
    const os = getOS();

    inlineHotkeyIdsRef.current.forEach(unregisterHotkey);
    inlineHotkeyIdsRef.current = [];

    const newIds: string[] = [];
    for (const hotkey of hotkeys) {
      const normalizedCombos = resolveCombos(hotkey.combos, os).map(
        normalizeCombo,
      );
      const effectiveScopeCheck = hotkey.scopeCheck || HotkeyScope.within;
      const hotkeyId = hotkey.id;

      for (const combo of normalizedCombos) {
        const registrationId = nextRegistrationId(hotkeyId);
        registerHotkey({
          id: registrationId,
          combo,
          comboKey: comboToKey(combo),
          callback: (e) => hotkeyCallbacksRef.current.get(hotkeyId)?.(e),
          contextNode: node,
          scopeCheck: effectiveScopeCheck,
          isEnabled: () => {
            const val = hotkeyIsEnabledRef.current.get(hotkeyId);
            return typeof val === "function" ? val() : (val ?? true);
          },
        });
        newIds.push(registrationId);
      }
    }

    inlineHotkeyIdsRef.current = newIds;
    attachGlobalKeyboardListener();

    return () => {
      inlineHotkeyIdsRef.current.forEach(unregisterHotkey);
      inlineHotkeyIdsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotkeyStructuralKey, node]);

  const restoreFocus = bridge ? State.getState().popFocusOrigin : undefined;

  const bridgeOriginRef = useRef(bridgeOrigin);
  bridgeOriginRef.current = bridgeOrigin;

  const focusParent = useCallback(() => {
    const parentId = node.parent?.id;
    if (!parentId) return false;
    const el = lastFocusedByContext.get(parentId);
    if (el?.isConnected) {
      el.focus({ preventScroll: true });
      return true;
    }
    return false;
  }, [node]);

  // -------------------------
  // onEscape hotkey
  // -------------------------

  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  const escapeHotkeyIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (escapeHotkeyIdRef.current) {
      unregisterHotkey(escapeHotkeyIdRef.current);
      escapeHotkeyIdRef.current = null;
    }
    if (!onEscape) return;

    const registrationId = nextRegistrationId("escape");
    escapeHotkeyIdRef.current = registrationId;
    registerHotkey({
      id: registrationId,
      combo: ESCAPE_COMBO,
      comboKey: ESCAPE_COMBO_KEY,
      callback: () => {
        // ``restoreFocusOnEscape: "none"`` lets the consumer restore after
        // teardown (e.g. TipTap unmount / panel collapse) — used by bridge
        // overlays and the thread panel.
        if (restoreFocusOnEscape !== "none") {
          if (bridge) {
            State.getState().popFocusOrigin();
          } else {
            focusParent();
          }
        }
        onEscapeRef.current?.();
      },
      contextNode: node,
      scopeCheck: HotkeyScope.within,
    });
    attachGlobalKeyboardListener();

    return () => {
      if (escapeHotkeyIdRef.current) {
        unregisterHotkey(escapeHotkeyIdRef.current);
        escapeHotkeyIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!onEscape, node, focusParent, bridge, restoreFocusOnEscape]);

  // Hands focus back out of this scope: to the element it came from - the
  // origin a bridged scope hangs off (left on the stack for whoever closes
  // the scope), or the last one focused in the parent context - and, with
  // ``restore: false`` or none of those still on the page, to the page
  // itself. Either way focus is not left inside. A scope letting go of it may
  // be collapsing and going inert, and keys pressed on an element there
  // reach nothing - not even the app's global shortcuts, which need only
  // focus to be somewhere live. (Browsers differ in whether they move focus
  // off an element that goes inert; this does not wait to find out.)
  const releaseFocus = useCallback(
    ({ restore = true }: { restore?: boolean } = {}) => {
      if (restore) {
        const origin = bridgeOriginRef.current?.elementRef;
        if (origin?.isConnected) {
          origin.focus({ preventScroll: true });
        } else {
          focusParent();
        }
      }
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        elementRef.current?.contains(active)
      ) {
        active.blur();
      }
    },
    [focusParent],
  );

  return {
    id,
    node,
    ref: refObject,
    Provider,
    Scope,
    restoreFocus,
    releaseFocus,
  };
}

// ==========================================================================
// useContextOverlay — shared overlay focus management for Popper, Dialog,
// and collapsing panels (e.g. ThreadOnlyPanelLayout).
// ==========================================================================
//
// Collapsing-but-mounted panels should mark their Scope ``inert`` while
// closed so editors inside (TipTap) cannot reclaim focus after
// ``restoreFocus`` / ``popFocusOrigin`` runs — Dialog avoids this by
// unmounting when closed.

interface UseOverlayOptions {
  isOpen: boolean;
  onClose: () => void;
  trapFocus?: boolean;
  /** Whether the overlay answers Escape at all. Defaults to true. */
  escapeable?: boolean;
  /**
   * What Escape does: ``close`` the overlay (the default), or ``unfocus`` -
   * hand focus back where it came from (``releaseFocus``) and stay open, for
   * a panel that sits beside the page rather than over it (the chat).
   */
  escape?: "close" | "unfocus";
  /** When true, auto-focuses the first focusable child element on open. */
  autoFocus?: boolean;
  /** Reference element to restore focus to when the overlay closes (Popper pattern). */
  focusRestoreRef?: React.RefObject<HTMLElement | null>;
  /** When true, uses bridge mode with focusOriginStack for focus restore (Dialog pattern). */
  bridge?: boolean;
  /** When this key changes while the overlay is open, re-run auto-focus so
   *  keyboard navigation keeps working after in-place content swaps. */
  contentKey?: string | null;
  /** When ``none``, skip focus restore on Escape so the consumer can restore
   *  after close/teardown (``focusRestoreRef`` / deferred ``popFocusOrigin``). */
  restoreFocusOnEscape?: "parent" | "none";
}

export function useContextOverlay(options: UseOverlayOptions) {
  const {
    isOpen,
    onClose,
    trapFocus = true,
    escapeable = true,
    escape = "close",
    autoFocus = false,
    focusRestoreRef,
    bridge = false,
    contentKey,
    restoreFocusOnEscape,
  } = options;

  // Escape set to unfocus lets go through the scope's own ``releaseFocus``,
  // which exists only once the scope does.
  const releaseFocusRef = useRef<() => void>(() => {});
  const scope = useContextScope({
    trapFocus: isOpen && trapFocus,
    resetHotkeys: isOpen,
    // Overlays over the page (poppers / dialogs) also block app-global
    // shortcuts like list j/k and node tab numbers while open. One beside the
    // page that leaves focus free (the chat) does not: ⌘K opens the palette
    // from inside it, as from anywhere else, and keys typed in its editor stay
    // there anyway.
    suppressGlobalHotkeys: isOpen && trapFocus,
    // One that traps focus owns the keyboard while open, focus in it or left
    // on its anchor; one that leaves focus free owns only the keys pressed in
    // it.
    claimsKeyboard: trapFocus,
    // Escape restores via focusParent (last focused element in the parent
    // context). ``focusRestoreRef`` covers non-Escape closes below.
    // Bridge mode always skips restore so popFocusOrigin / focusRestoreRef win.
    restoreFocusOnEscape:
      bridge || escape === "unfocus"
        ? "none"
        : (restoreFocusOnEscape ?? "parent"),
    onEscape:
      isOpen && escapeable
        ? escape === "unfocus"
          ? () => releaseFocusRef.current()
          : onClose
        : undefined,
    bridge,
  });
  releaseFocusRef.current = scope.releaseFocus;

  useContextHotkeys({
    id: `${scope.id}-overlay-submit`,
    combos: { mac: "meta+enter", windows: "ctrl+enter", linux: "ctrl+enter" },
    contextNode: scope.node,
    isEnabled: () => {
      if (!isOpen) return false;
      const submit = scope.ref.current?.querySelector<HTMLElement>(
        OVERLAY_SUBMIT_SELECTOR,
      );
      if (!submit) return false;
      if (submit instanceof HTMLButtonElement && submit.disabled) return false;
      return submit.getAttribute("aria-disabled") !== "true";
    },
    callback: (event) => {
      const submit = scope.ref.current?.querySelector<HTMLElement>(
        OVERLAY_SUBMIT_SELECTOR,
      );
      if (!submit) return;
      event.preventDefault();
      submit.click();
    },
  });

  // Auto-focus first focusable child on open, or when contentKey changes
  // while already open (e.g. command palette navigating between steps).
  useEffect(() => {
    if (!isOpen || !autoFocus) return;
    const container = scope.ref.current;
    if (!container) return;

    requestAnimationFrame(() => {
      if (
        container.contains(document.activeElement) &&
        document.activeElement !== container
      )
        return;
      const el = container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (el) el.focus();
      else container.focus();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, autoFocus, contentKey]);

  // Restore focus when overlay closes without Escape (outside-press / forceHide).
  const prevOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = isOpen;

    if (!wasOpen || isOpen) return;

    // Whatever restores focus below, a closed overlay never keeps it.
    if (bridge) {
      scope.restoreFocus?.();
      scope.releaseFocus({ restore: false });
      return;
    }

    if (!focusRestoreRef) {
      scope.releaseFocus({ restore: false });
      return;
    }
    requestAnimationFrame(() => {
      const refEl = focusRestoreRef.current;
      if (!refEl) {
        scope.releaseFocus({ restore: false });
        return;
      }

      const active = document.activeElement;
      // Focus already somewhere live - moved there by the user, or put back
      // in the restore target by Escape - stays. Only focus left on nothing,
      // or still in the overlay itself (on the page a moment as it goes), is
      // brought back.
      if (
        active &&
        active !== document.body &&
        document.contains(active) &&
        !scope.ref.current?.contains(active)
      ) {
        return;
      }

      // Itself where it takes focus - a list holding the keyboard on its rows
      // takes it at -1 - else the first thing in it that does: given that
      // list, the first was its first row, and the keyboard left the one it
      // was on.
      const focusTarget =
        refEl.matches(FOCUSABLE_SELECTOR) || refEl.hasAttribute("tabindex")
          ? refEl
          : refEl.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusTarget) focusTarget.focus();
      else if (refEl.tabIndex >= 0) refEl.focus();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, bridge]);

  return {
    Scope: scope.Scope,
    restoreFocus: scope.restoreFocus,
    releaseFocus: scope.releaseFocus,
    node: scope.node,
  };
}

// ==========================================================================
// useContextFocus — focus tracking relative to a context node
// ==========================================================================

export function useContextFocus(
  node: ContextNode,
  elementRef: React.RefObject<HTMLElement | null>,
) {
  const [focusState, setFocusState] = useState(FOCUS_OUTSIDE);

  useEffect(() => {
    attachGlobalFocusListener();

    const onFocusChange = () => {
      if (!elementRef.current) return;
      const activeElement = document.activeElement;

      if (!activeElement || activeElement === document.body) return;

      const activeNode = findNearestContextNode(activeElement);

      if (elementRef.current.contains(activeElement)) {
        const hasDesc = activeNode
          ? countDescendantDepth(node, activeNode) >= 2
          : false;
        setFocusState((prev) => {
          if (prev.isFocusedWithin && prev.hasActiveDescendant === hasDesc)
            return prev;
          return { isFocusedWithin: true, hasActiveDescendant: hasDesc };
        });
        return;
      }

      if (!activeNode) {
        setFocusState((prev) => {
          if (!prev.isFocusedWithin && !prev.hasActiveDescendant) return prev;
          return FOCUS_OUTSIDE;
        });
        return;
      }

      const depth = countDescendantDepth(node, activeNode);
      if (depth >= 0) {
        const hasDesc = depth >= 2;
        setFocusState((prev) => {
          if (prev.isFocusedWithin && prev.hasActiveDescendant === hasDesc)
            return prev;
          return { isFocusedWithin: true, hasActiveDescendant: hasDesc };
        });
      } else {
        setFocusState((prev) => {
          if (!prev.isFocusedWithin && !prev.hasActiveDescendant) return prev;
          return FOCUS_OUTSIDE;
        });
      }
    };

    focusSubscribers.add(onFocusChange);
    onFocusChange();

    return () => {
      focusSubscribers.delete(onFocusChange);
    };
  }, [node, elementRef]);

  return focusState;
}

// ==========================================================================
// useContextCellNavigation — grid keyboard navigation with hotkey registration
// ==========================================================================

export function useContextCellNavigation(
  id: string,
  elementRef: React.RefObject<HTMLElement | null>,
  node: ContextNode,
  options?: CellNavigationOptions & { tab?: boolean },
) {
  const cellNav = useCellRegistry(id, elementRef);

  const cellNavigationRef = useRef(options);
  cellNavigationRef.current = options;

  const cellNavStructuralKey = options
    ? JSON.stringify({
        rowCount: options.rowCount,
        colCount:
          typeof options.colCount === "function" ? "fn" : options.colCount,
        wrap: options.wrap,
        tab: options.tab,
        scrollBuffer: options.scrollBuffer,
      })
    : null;

  const cellNavHotkeyIdsRef = useRef<string[]>([]);
  const isCellFocusActive = useCallback(() => {
    const activeElement = document.activeElement as HTMLElement | null;
    if (!activeElement) return false;
    const contextContainer = elementRef.current;
    if (!contextContainer?.contains(activeElement)) return false;
    if (contextContainer === activeElement) return false;

    let cellWrapper: HTMLElement | null = activeElement;
    while (cellWrapper && cellWrapper.dataset.rowIndex === undefined) {
      cellWrapper = cellWrapper.parentElement;
      if (cellWrapper && !cellWrapper.closest(`[data-context-id="${id}"]`)) {
        return false;
      }
    }

    return cellWrapper?.dataset.rowIndex !== undefined;
  }, [elementRef, id]);

  useEffect(() => {
    cellNavHotkeyIdsRef.current.forEach(unregisterHotkey);
    cellNavHotkeyIdsRef.current = [];

    if (!cellNavStructuralKey) return;

    const directions = ["up", "down", "left", "right"] as const;
    const newIds: string[] = [];

    for (const dir of directions) {
      const handler = cellNav.createArrowKeyHandler(
        dir,
        cellNavigationRef.current,
      );
      const combos: string[][] = [];

      if (dir === "up") {
        combos.push(normalizeCombo("ArrowUp"), normalizeCombo("Up"));
      } else if (dir === "down") {
        combos.push(normalizeCombo("ArrowDown"), normalizeCombo("Down"));
      } else if (dir === "left") {
        combos.push(normalizeCombo("ArrowLeft"));
        if (cellNavigationRef.current?.tab)
          combos.push(normalizeCombo("Shift+Tab"));
      } else {
        combos.push(normalizeCombo("ArrowRight"));
        if (cellNavigationRef.current?.tab) combos.push(normalizeCombo("Tab"));
      }

      for (const combo of combos) {
        const registrationId = nextRegistrationId(`cellnav-${dir}`);
        registerHotkey({
          id: registrationId,
          combo,
          comboKey: comboToKey(combo),
          callback: handler,
          contextNode: node,
          scopeCheck: HotkeyScope.within,
          isEnabled: isCellFocusActive,
        });
        newIds.push(registrationId);
      }
    }

    cellNavHotkeyIdsRef.current = newIds;
    attachGlobalKeyboardListener();

    return () => {
      cellNavHotkeyIdsRef.current.forEach(unregisterHotkey);
      cellNavHotkeyIdsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellNavStructuralKey, node, isCellFocusActive]);

  return cellNav;
}

// ==========================================================================
// useContextTree — backward-compatible composition of the three hooks above
// ==========================================================================

interface UseContextTreeOptions extends UseContextScopeOptions {
  trackFocus?: boolean;
  cellNavigation?: CellNavigationOptions & {
    tab?: boolean;
  };
}

export function useContextTree(options: UseContextTreeOptions = {}) {
  const { trackFocus = false, cellNavigation, ...scopeOptions } = options;

  const scope = useContextScope(scopeOptions);
  const cellNav = useContextCellNavigation(
    scope.id,
    scope.ref,
    scope.node,
    cellNavigation,
  );
  const focusState = useContextFocus(scope.node, scope.ref);

  return {
    ...scope,
    isFocusedWithin: trackFocus ? focusState.isFocusedWithin : false,
    hasActiveDescendant: trackFocus ? focusState.hasActiveDescendant : false,
    ...cellNav,
  };
}

// Re-export public API from hotkey module
export {
  useContextHotkeys,
  normalizeCombo,
  getContextDistance,
  setHotkeysSuspended,
  areHotkeysSuspended,
} from "./useContextTree.hotkeys";
export type {
  HotkeyCallback,
  ScopeCheckFn,
  HotkeyComboInput,
  HotkeyConfig,
} from "./useContextTree.hotkeys";
